import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AvatarPreview } from "../components/AvatarEditor";
import { newRoomCode } from "../multiplayer/types";
import { QuizzleHost } from "./QuizzleHost";
import { RaceLobby } from "./RaceLobby";
import { DrawHost } from "./DrawHost";
import { useLocalProfile } from "../local/useLocalProfile";
import { createGuestProfile } from "../local/guestUser";
import { useClassRoom } from "../classroom/useClassRoom";
import { getTeacherClass, rememberTeacherClass, type ClassJoinOpts } from "../classroom/types";

/** Teacher control panel — the host of a persistent class. Shows the join code,
 *  the live roster, and controls to launch activities / return to lobby.
 *  (Phase 1: the launch buttons set the class mode; wiring the actual game
 *  join lands in the next phase.) */
export function ClassHost() {
  const nav = useNavigate();
  const { profile, setProfile } = useLocalProfile();
  const saved = useMemo(() => getTeacherClass(), []);
  const code = useMemo(() => saved.code || newRoomCode(), [saved.code]);
  const [className, setClassName] = useState(saved.name || "");
  const [editingName, setEditingName] = useState(!saved.name);

  useEffect(() => { rememberTeacherClass(code, className); }, [code, className]);
  // The teacher device needs a profile for the embedded Quizzle presenter.
  useEffect(() => { if (!profile) setProfile(createGuestProfile()); }, [profile, setProfile]);

  const joinOpts = useMemo<ClassJoinOpts | null>(
    () => (!editingName ? { code, role: "teacher", hostId: "teacher", className } : null),
    [code, className, editingName],
  );
  const { client, state, error } = useClassRoom(joinOpts);

  if (editingName) {
    return (
      <div className="class-host class-host-setup">
        <h1 className="class-host-title">👩‍🏫 New Class</h1>
        <form className="class-entry-form" onSubmit={(e) => { e.preventDefault(); if (className.trim()) setEditingName(false); }}>
          <label>Class name</label>
          <input className="class-code-input class-name-input" value={className} onChange={(e) => setClassName(e.target.value)} placeholder="e.g. Grade 3B" maxLength={40} autoFocus />
          <p className="class-dim">Class code: <strong>{code}</strong> — students enter this once.</p>
          <button className="btn btn-green btn-big btn-block" type="submit" disabled={!className.trim()}>Open class →</button>
          <button className="btn btn-ghost" type="button" onClick={() => nav("/class")}>← Back</button>
        </form>
      </div>
    );
  }

  const roster = state?.roster ?? [];
  const online = new Set(state?.onlineIds ?? []);
  const mode = state?.mode ?? "lobby";

  const launch = (m: "quizzle" | "word-runner" | "draw-it") => {
    const activityCode = mode === m && state?.activityCode ? state.activityCode : newRoomCode();
    client?.setMode(m, activityCode);
  };

  // Activity running → embed it, with a persistent Back-to-Lobby bar that
  // pulls the whole class back.
  if (mode !== "lobby" && state?.activityCode) {
    return (
      <div className="class-activity">
        <div className="class-activity-bar">
          <button className="btn btn-ghost" onClick={() => client?.backToLobby()}>← Class Lobby</button>
          <span className="class-activity-title">{className} · {online.size} online</span>
          <span className="class-code-mini">Join {code}</span>
        </div>
        {mode === "quizzle" && <QuizzleHost codeOverride={state.activityCode} embedded />}
        {mode === "draw-it" && <DrawHost codeOverride={state.activityCode} embedded />}
        {mode === "word-runner" && profile && (
          <RaceLobby
            codeOverride={state.activityCode}
            identity={{ id: `teacher_${code}`, displayName: profile.displayName, avatarConfig: profile.avatarConfig }}
            embedded
            asHost
          />
        )}
      </div>
    );
  }

  return (
    <div className="class-host">
      <header className="class-host-top">
        <button className="btn btn-ghost" onClick={() => nav("/class")}>← Exit</button>
        <h1 className="class-host-title">{className || "Class"}</h1>
        <div className="class-code-card">
          <span className="class-code-label">Join code</span>
          <span className="class-code-value">{code}</span>
        </div>
      </header>

      {error && <p className="class-err">⚠️ {error}</p>}

      <p className="class-host-mode class-dim">Pick an activity — every student's screen follows you.</p>
      <div className="class-host-controls">
        <button className="class-ctrl is-active" onClick={() => client?.backToLobby()}>
          <span aria-hidden>🏠</span> Lobby
        </button>
        <button className="class-ctrl" onClick={() => launch("quizzle")}>
          <span aria-hidden>🧩</span> Quizzle
        </button>
        <button className="class-ctrl" onClick={() => launch("word-runner")}>
          <span aria-hidden>🏃</span> Word Runner
        </button>
        <button className="class-ctrl" onClick={() => launch("draw-it")}>
          <span aria-hidden>🎨</span> DrawIt
        </button>
      </div>

      <section className="class-host-roster">
        <h2>Students <span className="class-dim">({online.size} online / {roster.length})</span></h2>
        <div className="class-roster-grid">
          {roster.map((s) => (
            <div key={s.id} className={`class-roster-card ${online.has(s.id) ? "is-online" : ""}`}>
              <span className="class-roster-avatar"><AvatarPreview cfg={s.avatarConfig} /></span>
              <strong>{s.name}</strong>
              <button className="class-roster-remove" onClick={() => client?.removeStudent(s.id)} aria-label={`Remove ${s.name}`}>×</button>
            </div>
          ))}
          {roster.length === 0 && <p className="class-dim">No students yet — share code <strong>{code}</strong>.</p>}
        </div>
      </section>
    </div>
  );
}
